import React, {
  useState,
  useEffect,
  useReducer,
  useMemo,
  useContext,
} from 'react';
import isEmpty from 'lodash/isEmpty';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import { reducer } from '../../state/reducer';
import { setSubjectInfo, setLoadingQuiz } from '../../state/actions';
import Context from '../../state/context';
import { tabIds, ACTIONS } from '../../utils/constants';
import { getAssessmentData } from '../../utils/index';
import {
  findQBSubject,
  findPREAPSubject,
} from 'legacy-src/components/learnosity/utils/helpers';
import useLocalStorage from 'chameleon/ui-stack/ui/base/hooks/use-local-storage';

const QuizDataProvider = (WrappedComponent) => {
  const Provider = (props) => {
    const {
      featureFlags,
      router,
      params,
      me,
      teacherSections,
      routes,
      isAdminOrCoordinator,
      flattenHierarchy,
      client,
      chosenEducationPeriodCode,
      subjects,
      userSubjects,
      subjectInfo: subjectInfoProp,
      userData,
    } = props;

    const qbContextAlreadyCreated = useContext(Context);
    const { teacherSubjects } = userData || {};

    const {
      location: {
        query: { quizId, activeTab: activeTabQuery, editMode },
      },
    } = router;

    const subject = userSubjects || teacherSubjects;
    const subjectInfo =
      findQBSubject(subject, params.subject) ||
      findPREAPSubject(userSubjects, params.subject) ||
      subjectInfoProp;
    const [isLoading, setIsLoading] = useState(true);

    // Preload Question Bank columns from localStorage
    const [, , getItemsColumns] = useLocalStorage({
      key: 'qb-table-columns-v2',
      itemKey: `${params.subject}-${me.initId}`,
    });
    const itemsColumns = useMemo(() => {
      const persisted = getItemsColumns();
      if (!persisted) return {};
      return JSON.parse(persisted);
    }, [params.subject, me.initId]);

    let initialValue = {
      activeTab: tabIds.BROWSE,
      isDirty: false,
      triggerSave: null,
      featureFlags,
      quizTitle: null,
      quizId: null,
      myQuizPending: {},
      hasMyQuizPending: false,
      isMyQuizGettingData: false,
      myQuiz: [],
      items: [],
      questions: {},
      // Preload Question Bank columns from localStorage
      itemsColumns,
      stimulusQuestionsInQuiz: {},
      allStimulusQuestions: {},
      activeStimulusFilters: [],
      teacherSections,
      router,
      routes,
      params,
      isAdminOrCoordinator,
      me,
      subjectInfo,
      isPreApSubject: false,
      flattenHierarchy,
      isRottenAssessment: false,
      client,
      chosenEducationPeriodCode,
      quizReviewTabLoading: false,
      subjects,
      isAutoBuildLoading: false,
      isLoadingQuiz: false,
    };

    const reducerProvider = useReducer(reducer, initialValue);
    const [state, dispatch] = reducerProvider;
    const { activeTab, isRottenAssessment } = state;

    useEffect(() => {
      if (subjectInfo) {
        setSubjectInfo(dispatch, {
          subjectInfo,
        });
      }
    }, [subjectInfo]);

    useEffect(() => {
      if (subjects && subjects.length) {
        dispatch({
          type: ACTIONS.SET_SUBJECTS,
          payload: {
            subjects,
          },
        });
      }
    }, [JSON.stringify(subjects)]);

    useEffect(() => {
      dispatch({
        type: ACTIONS.SET_TEACHER_SECTIONS,
        payload: {
          teacherSections,
        },
      });
    }, [teacherSections]);

    useEffect(() => {
      const setupData = async () => {
        setLoadingQuiz(dispatch, {
          isLoadingQuiz: true,
        });

        const data = await getAssessmentData(
          subjectInfo.initId,
          chosenEducationPeriodCode,
          quizId,
          client,
        );
        const shouldSetUpData = data && !data.isPracticeExam && data.isOwner;

        if (shouldSetUpData) {
          dispatch({
            type: ACTIONS.SET_EDIT_ASSESSMENT_PROPS,
            payload: {
              ...data,
              activeTab:
                activeTabQuery || routes[3].path === 'questions'
                  ? tabIds.BROWSE
                  : tabIds.QUIZ_BUILDER,
            },
          });
        }
        setIsLoading(false);
        setLoadingQuiz(dispatch, {
          isLoadingQuiz: false,
        });
      };

      if (!subjectInfo) {
        return;
      }

      // don't call quizEdit graphql for when editMode pass and is false and quizId
      const loadEditData = editMode ? editMode !== 'false' : true;
      if (quizId && loadEditData) {
        setupData();
      } else {
        setIsLoading(false);
      }
    }, [quizId, subjectInfo, editMode]);

    if (isLoading || isEmpty(subjectInfo)) {
      return <LoadingSpinner />;
    }

    if (isRottenAssessment) {
      throw new Error('Rotten assessment');
    }

    // if we try to initilize QB Context when it is already existed, use created one
    // useful for the assessment preview where use can edit quiz in the question bank and
    // on the my assignments (un-assigned). Editing on question bank should update state
    // of questions in the listManager which is created already with QBContext.

    // Editing Quiz on My assignment should create new QBContext just for current assessment.

    // context is [state, dispatcher]
    if (qbContextAlreadyCreated.length === 2) {
      return <WrappedComponent {...props} activeTab={activeTab} />;
    }

    return (
      <Context.Provider value={reducerProvider}>
        <WrappedComponent {...props} activeTab={activeTab} />
      </Context.Provider>
    );
  };

  return Provider;
};

export default QuizDataProvider;
